import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Menu,
  MenuItem,
  Button,
  Badge
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  ExpandMore as ExpandMoreIcon,
  EventNote as LeaveIcon,
  Inventory as AssetIcon,
  PeopleAlt as HRIcon,
  Receipt as ExpenseIcon,
  Description as FormIcon,
  DateRange as CalendarIcon,
  Inbox as InboxIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
import { useAuth } from '../auth/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useInbox } from '../context/InboxContext';

interface NavigationItem {
  label: string;
  icon?: React.ReactNode;
  path?: string;
  submenu?: NavigationItem[];
  roles?: string[];
}

const TopNavigation: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { isDarkMode } = useTheme();
  const { unreadCount } = useInbox();
  const [anchorEls, setAnchorEls] = useState<{ [key: string]: HTMLElement | null }>({});
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const navRef = React.useRef<HTMLDivElement>(null);

  const themeColors = {
    surface: isDarkMode ? '#1e293b' : '#f8fafc',
    text: isDarkMode ? '#f1f5f9' : '#1e293b',
    textSecondary: isDarkMode ? '#cbd5e1' : '#64748b',
    textTertiary: isDarkMode ? '#94a3b8' : '#94a3b8',
    border: isDarkMode ? '#334155' : '#e2e8f0',
    accent: '#9358f7',
    accentLight: '#a066f9',
    accentHover: isDarkMode ? 'rgba(147, 88, 247, 0.15)' : 'rgba(147, 88, 247, 0.1)',
    accentHoverDark: isDarkMode ? 'rgba(147, 88, 247, 0.2)' : 'rgba(147, 88, 247, 0.12)'
  };

  const navigationMenuItems: NavigationItem[] = [
    {
      label: 'Dashboard',
      icon: <DashboardIcon sx={{ fontSize: '18px' }} />,
      path: '/dashboard',
      roles: ['admin', 'user', 'manager']
    },
    {
      label: 'My Inbox',
      icon: (
        <Badge
          badgeContent={unreadCount > 0 ? unreadCount : undefined}
          sx={{
            '& .MuiBadge-badge': {
              backgroundColor: '#f44336',
              color: '#ffffff',
              fontSize: '10px',
              height: '16px',
              minWidth: '16px',
              borderRadius: '8px'
            }
          }}
        >
          <InboxIcon sx={{ fontSize: '18px' }} />
        </Badge>
      ),
      path: '/inbox',
      roles: ['admin', 'user', 'manager']
    },
    {
      label: 'Leave Management',
      icon: <LeaveIcon sx={{ fontSize: '18px' }} />,
      roles: ['admin', 'user', 'manager'],
      submenu: [
        { label: 'Dashboard', path: '/leaves/dashboard', roles: ['admin', 'manager'] },
        { label: 'Apply Leave', path: '/leaves/apply', roles: ['user', 'manager', 'admin'] },
        { label: 'My Leaves', path: '/leaves/my-leaves', roles: ['user', 'manager', 'admin'] }
      ]
    },
    {
      label: 'Asset Management',
      icon: <AssetIcon sx={{ fontSize: '18px' }} />,
      roles: ['admin', 'user'],
      submenu: [
        { label: 'View Assets', path: '/assets/view', roles: ['admin', 'manager'] },
        { label: 'Manage Asset', path: '/assets/assign', roles: ['admin'] },
        { label: 'My Assets', path: '/assets/my-assets', roles: ['user', 'admin', 'manager'] }
      ]
    },
    {
      label: 'HR Management',
      icon: <HRIcon sx={{ fontSize: '18px' }} />,
      path: '/hr/employees',
      roles: ['admin']
    },
    {
      label: 'Resource Directory',
      icon: <HRIcon sx={{ fontSize: '18px' }} />,
      path: '/resources/directory',
      roles: ['admin', 'manager']
    },
    {
      label: 'Expense Claims',
      icon: <ExpenseIcon sx={{ fontSize: '18px' }} />,
      roles: ['admin', 'user', 'manager'],
      submenu: [
        { label: 'Dashboard', path: '/expenses/dashboard', roles: ['admin', 'manager'] },
        { label: 'My Claims', path: '/expenses/my-claims', roles: ['user', 'manager', 'admin'] }
      ]
    },
    {
      label: 'Form Management',
      icon: <FormIcon sx={{ fontSize: '18px' }} />,
      roles: ['admin', 'user'],
      submenu: [
        { label: 'Manage Forms', path: '/forms/manage', roles: ['admin'] },
        { label: 'My Forms', path: '/forms/my-forms', roles: ['user', 'admin'] }
      ]
    },
    {
      label: 'Holiday Management',
      icon: <CalendarIcon sx={{ fontSize: '18px' }} />,
      roles: ['admin', 'user'],
      submenu: [
        { label: 'Holiday Calendar', path: '/holidays/calendar', roles: ['user', 'admin'] },
        { label: 'Manage Holidays', path: '/holidays/manage', roles: ['admin'] }
      ]
    },
    {
      label: 'Ticket Management',
      icon: <CalendarIcon sx={{ fontSize: '18px' }} />,
      roles: ['admin', 'user', 'manager'],
      submenu: [
        { label: 'Raise Ticket', path: '/tickets/raise', roles: ['admin', 'user', 'manager'] },
        { label: 'My Tickets', path: '/tickets/my-tickets', roles: ['admin', 'user', 'manager'] }
      ]
    }
  ];

  const isMenuVisible = (item: NavigationItem): boolean => {
    if (!item.roles) return true;
    return item.roles.includes(user?.role || '');
  };

  const filterMenuItems = (items: NavigationItem[]): NavigationItem[] => {
    return items.filter(isMenuVisible).map((item) => ({
      ...item,
      submenu: item.submenu ? filterMenuItems(item.submenu) : undefined
    }));
  };

  const visibleMenuItems = filterMenuItems(navigationMenuItems);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, menuLabel: string) => {
    setAnchorEls((prev) => ({
      ...prev,
      [menuLabel]: event.currentTarget
    }));
  };

  const handleMenuClose = (menuLabel: string) => {
    setAnchorEls((prev) => ({
      ...prev,
      [menuLabel]: null
    }));
  };

  const handleNavigate = (path: string, menuLabel: string) => {
    navigate(path);
    handleMenuClose(menuLabel);
  };

  const isActive = (path?: string) => {
    if (!path) return false;
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const checkScroll = () => {
    if (navRef.current) {
      setCanScrollLeft(navRef.current.scrollLeft > 0);
      setCanScrollRight(
        navRef.current.scrollLeft < navRef.current.scrollWidth - navRef.current.clientWidth
      );
    }
  };

  const scrollLeft = () => {
    if (navRef.current) {
      navRef.current.scrollBy({ left: -200, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    if (navRef.current) {
      navRef.current.scrollBy({ left: 200, behavior: 'smooth' });
    }
  };

  React.useEffect(() => {
    const nav = navRef.current;
    if (nav) {
      nav.addEventListener('scroll', checkScroll);
      checkScroll(); // Initial check
      return () => nav.removeEventListener('scroll', checkScroll);
    }
    return undefined;
  }, []);

  return (
    <Box
      sx={{
        display: { xs: 'none', sm: 'flex' },
        alignItems: 'center',
        position: 'sticky',
        top: 64,              // height of ResponsiveNavbar
        zIndex: 1200,
        backgroundColor: themeColors.surface,
        borderBottom: `1px solid ${themeColors.border}`,
      }}
    >
      {/* Left Arrow */}
      <Button
        onClick={scrollLeft}
        disabled={!canScrollLeft}
        sx={{
          minWidth: 'auto',
          p: 1,
          color: canScrollLeft ? themeColors.text : themeColors.textTertiary,
          '&:hover': {
            backgroundColor: canScrollLeft ? themeColors.accentHover : 'transparent',
          },
          '&:disabled': {
            color: themeColors.textTertiary,
          }
        }}
      >
        <ChevronLeftIcon />
      </Button>

      {/* Navigation Container */}
      <Box
        ref={navRef}
        sx={{
          flex: 1,
          overflowX: 'auto',
          overflowY: 'hidden',
          px: { xs: 0, sm: 0 },
          py: 0.5,
          '&::-webkit-scrollbar': {
            height: '4px'
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {visibleMenuItems.map((item, index) => (
            <div key={index}>
              {item.submenu ? (
                <>
                  <Button
                    onClick={(e) => handleMenuOpen(e, item.label)}
                    startIcon={item.icon}
                    endIcon={<ExpandMoreIcon sx={{ fontSize: '18px' }} />}
                    sx={{
                      color: (item.submenu ? item.submenu.some(sub => isActive(sub.path)) : isActive(item.path)) ? themeColors.accent : themeColors.text,
                      textTransform: 'none',
                      fontSize: '14px',
                      fontWeight: (item.submenu ? item.submenu.some(sub => isActive(sub.path)) : isActive(item.path)) ? 600 : 500,
                      px: 1.5,
                      py: 1,
                      transition: 'all 150ms ease-in-out',
                      borderBottom: (item.submenu ? item.submenu.some(sub => isActive(sub.path)) : isActive(item.path)) ? `3px solid ${themeColors.accent}` : 'none',
                      borderRadius: 0,
                      whiteSpace: 'nowrap',
                      '&:hover': {
                        backgroundColor: themeColors.accentHover,
                        color: themeColors.accent
                      }
                    }}
                  >
                    {item.label}
                  </Button>
                  <Menu
                    anchorEl={anchorEls[item.label]}
                    open={Boolean(anchorEls[item.label])}
                    onClose={() => handleMenuClose(item.label)}
                    slotProps={{
                      paper: {
                        sx: {
                          backgroundColor: themeColors.surface,
                          color: themeColors.text,
                          border: `1px solid ${themeColors.border}`
                        }
                      }
                    }}
                  >
                    {item.submenu?.map((subitem, subindex) => (
                      <MenuItem
                        key={subindex}
                        onClick={() => handleNavigate(subitem.path || '/', item.label)}
                        selected={isActive(subitem.path)}
                        sx={{
                          fontSize: '13px',
                          color: isActive(subitem.path) ? themeColors.accent : themeColors.text,
                          backgroundColor: isActive(subitem.path) ? themeColors.accentHoverDark : 'transparent',
                          '&:hover': {
                            backgroundColor: themeColors.accentHover
                          }
                        }}
                      >
                        {subitem.label}
                      </MenuItem>
                    ))}
                  </Menu>
                </>
              ) : (
                <Button
                  startIcon={item.icon}
                  onClick={() => navigate(item.path || '/')}
                  sx={{
                    color: isActive(item.path) ? themeColors.accent : themeColors.text,
                    textTransform: 'none',
                    fontSize: '14px',
                    fontWeight: isActive(item.path) ? 600 : 500,
                    px: 1.5,
                    py: 1,
                    transition: 'all 150ms ease-in-out',
                    borderBottom: isActive(item.path) ? `3px solid ${themeColors.accent}` : 'none',
                    borderRadius: 0,
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      backgroundColor: themeColors.accentHover,
                      color: themeColors.accent
                    }
                  }}
                >
                  {item.label}
                </Button>
              )}
            </div>
          ))}
        </Box>
      </Box>

      {/* Right Arrow */}
      <Button
        onClick={scrollRight}
        disabled={!canScrollRight}
        sx={{
          minWidth: 'auto',
          p: 1,
          color: canScrollRight ? themeColors.text : themeColors.textTertiary,
          '&:hover': {
            backgroundColor: canScrollRight ? themeColors.accentHover : 'transparent',
          },
          '&:disabled': {
            color: themeColors.textTertiary,
          }
        }}
      >
        <ChevronRightIcon />
      </Button>
    </Box>
  );
};

export default TopNavigation;
